import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPeerSurvey, submitPeerAssessment } from '../services/api';
import { getPeerQuestions } from '../data/surveyQuestions';
import { calculateScores } from '../services/scoringService';
import SurveyQuestions from './SurveyQuestions';
import '../styles/main.css';

function PeerSurveyPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [surveyData, setSurveyData] = useState(null);
  const [responses, setResponses] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getPeerSurvey(token);
      setSurveyData(data);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Invalid or expired survey link');
      setLoading(false);
    }
  };

  const handleSubmit = async (surveyResponses) => {
    try {
      setLoading(true);
      setError('');
      
      // Calculate scores
      const scores = calculateScores(surveyResponses);
      
      // Submit to backend
      await submitPeerAssessment(token, surveyResponses, scores);
      
      setSubmitted(true);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to submit assessment');
      setLoading(false);
    }
  };

  if (loading && !surveyData) {
    return (
      <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading survey...</h2>
      </div>
    );
  }

  if (error && !surveyData) {
    return (
      <div className="container" style={{ padding: '2rem' }}>
        <div className="error-message" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2>Survey Not Available</h2>
          <p>{error}</p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
            This survey link may have expired or already been completed. Please contact the person who sent you the invitation if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="container" style={{ padding: '2rem' }}>
        <div className="success-container" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            background: '#d1fae5',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ color: '#10b981', marginBottom: '1rem' }}>✓ Thank You!</h2>
            <p style={{ fontSize: '1.1rem', color: '#333' }}>
              Your feedback for <strong>{surveyData.userName}</strong> has been submitted successfully.
            </p>
          </div>
          
          <div style={{ textAlign: 'left', background: 'white', padding: '1.5rem', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem' }}>What happens next?</h3>
            <ul style={{ lineHeight: 1.8, color: '#666' }}>
              <li>Your responses are anonymous and will be combined with other feedback</li>
              <li>{surveyData.userName} will see averaged results from all assessors</li>
              <li>This helps them understand how others perceive their work style</li>
            </ul>
          </div>

          <div style={{ marginTop: '2rem' }}>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Want to discover your own Creative Work Footprint?
            </p>
            <a 
              href="/" 
              style={{
                display: 'inline-block',
                marginTop: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: '#0066a1',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: 600
              }}
            >
              Take the Assessment
            </a>
          </div>
        </div>
      </div>
    );
  }

  const peerQuestions = surveyData ? getPeerQuestions(surveyData.firstName || surveyData.userName) : [];

  return (
    <div className="container">
      <div className="peer-survey-header" style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ marginBottom: '0.5rem', color: '#0066a1' }}>
          360° Feedback for {surveyData.userName}
        </h1>
        <p style={{ color: '#666', fontSize: '1rem' }}>
          Please answer the following questions about {surveyData.firstName || surveyData.userName}'s work style. 
          Your responses will be anonymous and help them gain insights into how others perceive their creative work approach.
        </p>
        <p style={{ color: '#999', fontSize: '0.9rem', marginTop: '0.75rem' }}>
          This survey takes about 5 minutes to complete.
        </p>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      <SurveyQuestions
        questions={peerQuestions}
        peerName={surveyData.firstName || surveyData.userName}
        onComplete={handleSubmit}
      />
    </div>
  );
}

export default PeerSurveyPage;
